import { useDroppable } from '@dnd-kit/core';
import {
    getCategorizedRulesAndTotals,
    prioritySortRulesAndGroups,
} from '@fruitful/money-map';
import {
    type DeepPartial,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useCallback, useMemo } from 'react';
import { SmoothieScrollView, Spinner, YStack } from 'smoothie';

import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import { type GoalPanelPreset } from '../utils/buildGoalPanelPresetGroupDto';
import { GoalsWaterfallList } from './goals-waterfall/GoalsWaterfallList';
import type { GoalSelectionState } from './goals-waterfall/goalsWaterfallTypes';
import type { GoalsWaterfallDnDControls } from './goals-waterfall/useGoalsWaterfallDnD';

interface MoneyMapperGoalsDevSectionProps {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onGoalSelected?: (selection: GoalSelectionState) => void;
    selectedGoal?: GoalSelectionState;
    goalsWaterfallDnd: GoalsWaterfallDnDControls;
    previewInsertIndex?: number | null;
    previewPreset?: GoalPanelPreset | null;
}

export const MoneyMapperGoalsDevSection = ({
    moneyMap,
    onGoalSelected,
    selectedGoal,
    goalsWaterfallDnd,
    previewInsertIndex = null,
    previewPreset = null,
}: MoneyMapperGoalsDevSectionProps) => {
    const { accounts } = useMoneyMapAccounts();
    const { setNodeRef: setDroppableRef } = useDroppable({
        id: 'goals-waterfall-drop-zone',
    });

    const { goalRules } = useMemo(
        () => getCategorizedRulesAndTotals(moneyMap, accounts),
        [JSON.stringify(moneyMap), JSON.stringify(accounts)],
    );

    const groups = useMemo(
        () => moneyMap.moneyMapRuleGroups ?? [],
        [JSON.stringify(moneyMap.moneyMapRuleGroups)],
    );

    const goalItems = useMemo(
        () => prioritySortRulesAndGroups(goalRules, groups),
        [goalRules, groups],
    );

    const topLevelIds = useMemo(
        () => goalItems.map((item) => item.id ?? ''),
        [goalItems],
    );

    const getGroupRules = useCallback(
        (groupId: string | undefined) =>
            goalRules.filter((r) => r.moneyMapRuleGroupId === groupId),
        [goalRules],
    );

    const hasGoals = goalItems.length > 0;

    return (
        <YStack flex={1}>
            <YStack flex={1} minHeight={0}>
                <div
                    ref={setDroppableRef}
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <SmoothieScrollView
                        containerStyle={{
                            marginHorizontal: 0,
                            $sm: { alignSelf: 'stretch' },
                            '$platform-web': { marginTop: 0, paddingBottom: 0 },
                        }}
                    >
                        <YStack paddingBottom={16} width="100%">
                            {!hasGoals && (
                                <YStack
                                    padding={24}
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Spinner />
                                </YStack>
                            )}
                            {hasGoals && (
                                <GoalsWaterfallList
                                    moneyMap={moneyMap}
                                    goalRules={goalRules}
                                    goalItems={goalItems}
                                    topLevelIds={topLevelIds}
                                    getGroupRules={getGroupRules}
                                    previewInsertIndex={previewInsertIndex}
                                    previewPreset={previewPreset}
                                    onSelectGoal={onGoalSelected}
                                    selectedGoal={selectedGoal}
                                    goalsWaterfallDnd={goalsWaterfallDnd}
                                />
                            )}
                        </YStack>
                    </SmoothieScrollView>
                </div>
            </YStack>
        </YStack>
    );
};
